import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    DetachedRouteHandle,
    ParamMap,
    PRIMARY_OUTLET,
    RouteReuseStrategy,
} from '@angular/router';
import { TabService } from '@/app/core/services/tab.service';
import { TabRouteCacheRegistry } from '@/app/core/routing/tab-route-cache.registry';
import { normalizeTabRouteKey } from '@/app/core/routing/tab-route-key';

/**
 * Custom Angular `RouteReuseStrategy` that keeps a routed component (and its DOM,
 * form state, loaded data) alive while its tab stays open, so switching away and
 * back to a tab does not rebuild the page from scratch.
 *
 * How it fits together:
 * - `TabService` owns the list of open tabs (`tabs()`), keyed by their full URL.
 * - `TabRouteCacheRegistry` is the actual store of detached component handles.
 * - This strategy is the bridge Angular calls into during every navigation to
 *   decide what to detach, store, reattach, or throw away.
 *
 * Two hard-won invariants this class must respect:
 *
 * 1. Only the primary-outlet leaf route participates in detach/store/attach.
 *    Angular invokes the strategy for every route level (parent + child). If we
 *    also cached parents, their handles would clobber the leaf handles and break
 *    reuse (components get recreated, HTTP requests re-run). See `isPrimaryLeafRoute`.
 *
 * 2. Angular asks the strategy to attach TWICE per navigation — once while it is
 *    building the future `RouterState` and again while it is activating routes.
 *    Therefore `retrieve()` MUST be side-effect free: it may be called and then
 *    discarded. Angular itself removes the entry by calling `store(route, null)`
 *    after it has actually attached the component. If `retrieve()` evicted on the
 *    first call, the second `shouldAttach()` would miss, Angular would build a
 *    brand-new component, and the tab's state would reset. This was the survey
 *    list "reset on close" bug.
 */
@Injectable()
export class TabRouteReuseStrategy implements RouteReuseStrategy {
    constructor(
        private readonly tabService: TabService,
        private readonly cache: TabRouteCacheRegistry,
    ) {}

    /**
     * Tells Angular whether the currently-rendered route can be reused in place for
     * the incoming navigation. Reuse is allowed when both point at the same route
     * config (same lazy component), which is Angular's default behaviour and avoids
     * needless teardown on same-route param changes.
     */
    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        return future.routeConfig === curr.routeConfig;
    }

    /**
     * Called when a route is being left. Return `true` to have Angular detach the
     * component (instead of destroying it) so we can restore it later.
     * We only detach a leaf whose URL is still an open tab — if the tab was closed,
     * we let the component be destroyed so it does not linger in memory.
     */
    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        if (!this.isPrimaryLeafRoute(route)) {
            return false;
        }
        const key = this.cacheKey(route);
        if (!this.isCacheableKey(key)) {
            return false;
        }
        return this.tabService.tabs().some((t) => normalizeTabRouteKey(t.route) === key);
    }

    /**
     * Stores (or clears) the detached component handle for a route.
     * Angular passes a real handle when parking a detached component, and passes
     * `null` after it has reattached one — per the `RouteReuseStrategy` contract a
     * `null` handle means "erase the stored value", so we delete the key rather
     * than caching `null` (a cached `null` would make `shouldAttach` lie and later
     * crash when Angular dereferences the missing handle).
     */
    store(_route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
        if (!this.isPrimaryLeafRoute(_route)) {
            return;
        }
        const key = this.cacheKey(_route);
        if (!this.isCacheableKey(key)) {
            return;
        }
        if (!handle) {
            this.cache.delete(key);
            return;
        }
        this.cache.set(key, handle);
    }

    /**
     * Tells Angular whether we have a stored component to reattach for the route
     * being activated. True only when a non-null handle exists for this URL.
     */
    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        if (!this.isPrimaryLeafRoute(route)) {
            return false;
        }
        const key = this.cacheKey(route);
        if (!this.isCacheableKey(key)) {
            return false;
        }
        return this.cache.get(key) != null;
    }

    /**
     * Returns the stored handle WITHOUT removing it (see invariant 2 in the class
     * doc). Angular calls this up to twice per navigation and then erases the entry
     * itself via `store(route, null)`.
     */
    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        if (!this.isPrimaryLeafRoute(route)) {
            return null;
        }
        const key = this.cacheKey(route);
        return this.cache.get(key) ?? null;
    }

    /**
     * The cache key for a route is its normalized full URL. Because Angular has
     * already advanced the snapshot before each strategy call, this yields the
     * source URL while deactivating and the destination URL while attaching, which
     * is exactly what we want — each tab caches under its own URL.
     */
    private cacheKey(route: ActivatedRouteSnapshot): string {
        return normalizeTabRouteKey(this.buildUrlFromSnapshot(route));
    }

    /**
     * Guards every entry point so only a concrete, primary-outlet leaf takes part
     * in caching: named outlets are ignored, non-leaf (parent) snapshots are
     * skipped to protect leaf handles, and routes without a component/lazy loader
     * (e.g. pure redirects) have nothing to detach.
     */
    private isPrimaryLeafRoute(route: ActivatedRouteSnapshot): boolean {
        if (route.outlet !== PRIMARY_OUTLET) {
            return false;
        }
        if (route.firstChild) {
            return false;
        }
        const cfg = route.routeConfig;
        if (!cfg) {
            return false;
        }
        return !!(cfg.loadComponent || cfg.component);
    }

    /**
     * Rebuilds the full URL from the snapshot tree so it matches the string Angular
     * emits as `NavigationEnd#urlAfterRedirects` and that `TabService` stores as
     * `TabItem.route`. Walks from the root down the primary-outlet chain collecting
     * path segments, collapses duplicate slashes, then appends sorted query params.
     */
    private buildUrlFromSnapshot(route: ActivatedRouteSnapshot): string {
        const parts: string[] = [];
        let n: ActivatedRouteSnapshot | null = route.root;
        while (n) {
            for (const u of n.url) {
                if (u.path.length > 0) {
                    parts.push(u.path);
                }
            }
            n = n.firstChild;
        }
        let path = parts.length > 0 ? '/' + parts.join('/') : '/';
        path = path.replace(/\/+/g, '/');
        const qs = this.serializeQueryParams(route.root.queryParamMap);
        return qs.length > 0 ? `${path}?${qs}` : path;
    }

    /** Serializes query params in key order so the cache key is stable regardless of param order. */
    private serializeQueryParams(qm: ParamMap): string {
        if (!qm.keys.length) {
            return '';
        }
        return qm.keys
            .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(qm.get(k) ?? '')}`)
            .join('&');
    }

    /**
     * Excludes routes that should never be cached: authentication pages and the
     * offline/no-connection page must always render fresh, never a stale detached copy.
     */
    private isCacheableKey(normalizedKey: string): boolean {
        const pathOnly = normalizedKey.split('?')[0] ?? normalizedKey;
        if (pathOnly.startsWith('/auth')) {
            return false;
        }
        if (pathOnly === '/no-connection' || pathOnly.startsWith('/no-connection/')) {
            return false;
        }
        return true;
    }
}
